import type { BoardNode, Connection } from './types';

export const DEFAULT_TILE_SNAP_LIMIT = 2;
export const FORK_TILE_SNAP_LIMIT = 4;
const START_TILE_SNAP_LIMIT = 1;

interface SnapPoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface NearestTileSnapRulesOptions {
  readonly getWorldPosition?: (node: BoardNode) => SnapPoint | null | undefined;
  /**
   * Spectator-arena island ID. Tiles belonging to this island only snap to other tiles
   * on the same island (no cross-snap to mainland or bridges); mainland tiles likewise
   * never snap to arena tiles. Provides hard isolation for the warmup arena.
   */
  readonly arenaIslandId?: string;
}

const SURFACE_PREFIX_RE = /^(island-\d+|bridge-\d+|floating)-/;

function nodeSurfacePrefix(node: BoardNode): string | null {
  const m = SURFACE_PREFIX_RE.exec(node.id);
  return m ? m[1] : null;
}

function isOnArenaIsland(node: BoardNode, arenaIslandId: string | undefined): boolean {
  if (arenaIslandId == null) return false;
  return nodeSurfacePrefix(node) === arenaIslandId;
}

function isWalkConnection(conn: Connection): boolean {
  return conn.type === 'walk';
}

function cloneConnection(conn: Connection): Connection {
  return { targetId: conn.targetId, type: conn.type };
}

function cloneNode(node: BoardNode): BoardNode {
  return {
    ...node,
    position: { ...node.position },
    connections: node.connections.map(cloneConnection),
  };
}

function pointForNode(node: BoardNode, options?: NearestTileSnapRulesOptions): SnapPoint {
  return options?.getWorldPosition?.(node) ?? node.position;
}

function squaredDistance(
  a: BoardNode,
  b: BoardNode,
  options?: NearestTileSnapRulesOptions,
): number {
  const aPoint = pointForNode(a, options);
  const bPoint = pointForNode(b, options);
  const dx = bPoint.x - aPoint.x;
  const dz = bPoint.z - aPoint.z;
  // Walk graph is a 2D proximity question — players walk between islands regardless of Y.
  // Y-separated tiles still snap if they're XZ-close.
  return dx * dx + dz * dz;
}

function addWalkConnection(fromNode: BoardNode, toNode: BoardNode): void {
  if (fromNode.connections.some((conn) => conn.targetId === toNode.id && isWalkConnection(conn))) {
    return;
  }
  fromNode.connections.push({ targetId: toNode.id, type: 'walk' });
}

function outgoingWalkCount(node: BoardNode): number {
  return node.connections.filter(isWalkConnection).length;
}

function maxOutgoingWalkCount(node: BoardNode): number {
  if (node.type === 'start') return START_TILE_SNAP_LIMIT;
  return getNearestTileSnapLimit(node);
}

function hasWalkConnection(fromNode: BoardNode, toNode: BoardNode): boolean {
  return fromNode.connections.some((conn) => conn.targetId === toNode.id && isWalkConnection(conn));
}

function canAddOutgoingWalkConnection(fromNode: BoardNode, toNode: BoardNode): boolean {
  if (fromNode.id === toNode.id) return false;
  if (hasWalkConnection(fromNode, toNode)) return true;
  return outgoingWalkCount(fromNode) < maxOutgoingWalkCount(fromNode);
}

function addPairedWalkConnection(fromNode: BoardNode, toNode: BoardNode): boolean {
  if (fromNode.type === 'start' || toNode.type === 'start') return false;
  if (
    !canAddOutgoingWalkConnection(fromNode, toNode) ||
    !canAddOutgoingWalkConnection(toNode, fromNode)
  ) {
    return false;
  }
  addWalkConnection(fromNode, toNode);
  addWalkConnection(toNode, fromNode);
  return true;
}

export function getNearestTileSnapLimit(node: Pick<BoardNode, 'isFork'>): number {
  return node.isFork === true ? FORK_TILE_SNAP_LIMIT : DEFAULT_TILE_SNAP_LIMIT;
}

export function isFloatingBoardNode(node: Pick<BoardNode, 'parent' | 'hostBridgeId'>): boolean {
  return node.parent === null && node.hostBridgeId == null;
}

/**
 * Builds the editor's nearest-tile walk graph in data form.
 *
 * Regular tiles consider their two nearest non-start neighbors; fork tiles consider four.
 * Non-start tiles connect as paired walk edges when both endpoints include each other in their
 * own top-K nearest sets. A final repair pass gives otherwise-isolated non-start tiles one paired
 * nearest legal edge, so dragging a tile far away cannot create a dead-end graph by itself. Start
 * tiles preserve spawn asymmetry by emitting exactly one outgoing walk edge to the nearest
 * non-start tile without requiring or adding a reverse edge.
 * Existing non-walk edges are preserved; existing walk edges are replaced.
 */
export function applyNearestTileSnapRules(
  nodes: readonly BoardNode[],
  options?: NearestTileSnapRulesOptions,
): BoardNode[] {
  const nextNodes = nodes.map((node) => {
    const cloned = cloneNode(node);
    cloned.connections = cloned.connections.filter((conn) => !isWalkConnection(conn));
    return cloned;
  });

  const snapSources = nextNodes.filter((node) => node.type !== 'pathing');
  const nextNodesById = new Map(nextNodes.map((node) => [node.id, node]));
  const topCandidateIdsBySourceId = new Map<string, Set<string>>();

  for (const source of snapSources) {
    const candidates = getNearestSnapCandidates(source, snapSources, options);
    const limit = source.type === 'start' ? START_TILE_SNAP_LIMIT : getNearestTileSnapLimit(source);
    topCandidateIdsBySourceId.set(
      source.id,
      new Set(candidates.slice(0, limit).map(({ node }) => node.id)),
    );
  }

  for (const source of snapSources) {
    const sourceTopCandidates = topCandidateIdsBySourceId.get(source.id);
    if (!sourceTopCandidates) continue;

    for (const targetId of sourceTopCandidates) {
      const target = nextNodesById.get(targetId);
      if (!target) continue;

      if (source.type === 'start') {
        addWalkConnection(source, target);
        continue;
      }

      if (!topCandidateIdsBySourceId.get(target.id)?.has(source.id)) {
        continue;
      }

      addPairedWalkConnection(source, target);
    }
  }

  for (const source of snapSources) {
    if (source.type === 'start' || outgoingWalkCount(source) > 0) continue;

    for (const { node: candidate } of getNearestSnapCandidates(source, snapSources, options)) {
      if (addPairedWalkConnection(source, candidate)) break;
    }
  }

  return nextNodes;
}

function getNearestSnapCandidates(
  source: BoardNode,
  snapSources: readonly BoardNode[],
  options?: NearestTileSnapRulesOptions,
): Array<{ node: BoardNode; distance: number }> {
  const arenaIslandId = options?.arenaIslandId;
  const sourceOnArena = isOnArenaIsland(source, arenaIslandId);
  return snapSources
    .filter((candidate) => {
      if (candidate.id === source.id) return false;
      if (candidate.type === 'start') return false;
      if (arenaIslandId != null) {
        const candOnArena = isOnArenaIsland(candidate, arenaIslandId);
        // Hard isolation: arena tiles only snap with arena tiles; mainland tiles never snap to arena.
        if (sourceOnArena !== candOnArena) return false;
      }
      return true;
    })
    .map((candidate) => ({
      node: candidate,
      distance: squaredDistance(source, candidate, options),
    }))
    .sort((a, b) => {
      if (a.distance !== b.distance) return a.distance - b.distance;
      return a.node.id.localeCompare(b.node.id);
    });
}
